import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Card, Stack, Typography,
} from '@mui/material';

// The payroll reports' charts. Hand-drawn rather than a chart library: a
// row of boxes and a few SVG paths is all these need, and every mark here
// follows the dataviz rules — one axis per chart, thin marks, one colour
// per meaning, a tooltip on every mark, weeks pickable by click or key.

// The series colours, one per meaning, the same in every chart: cost is
// the product blue, the tax part of it orange, revenue teal. Checked with
// the dataviz validator in both modes (lightness, chroma, colour-blind
// separation); teal is under 3:1 on white, so every bar has a tooltip and
// the numbers are in the tables below.
//
// Hiring borrows the same three for the same kind of meaning — blue for
// the people in the pipeline, teal for hires, orange for the ones lost —
// and adds the ways a candidacy ends without a hire, in the one order of
// those four hues that passes every adjacent check in both modes. Yellow
// and magenta are under 3:1 on white, so the loss bars carry their
// counts as text.
export const SERIES = {
  payroll: { light: '#0077FF', dark: '#3392FF' },
  tax: { light: '#EB6834', dark: '#D95926' },
  revenue: { light: '#1BAF7A', dark: '#199E70' },
  added: { light: '#0077FF', dark: '#3392FF' },
  hired: { light: '#1BAF7A', dark: '#199E70' },
  lost: { light: '#EB6834', dark: '#D95926' },
  unresponsive: { light: '#EB6834', dark: '#D95926' },
  declined: { light: '#6250D6', dark: '#9085E9' },
  withdrew: { light: '#EDA100', dark: '#C98500' },
  no_show: { light: '#E87BA4', dark: '#D55181' },
  // Weekly volume: this year against last. The payroll/tax pair, which
  // passes every check in both modes.
  thisYear: { light: '#0077FF', dark: '#3392FF' },
  lastYear: { light: '#EB6834', dark: '#D95926' },
  // The schedule: what happened, what is expected, what the roster can carry.
  actual: { light: '#0077FF', dark: '#3392FF' },
  forecast: { light: '#6250D6', dark: '#9085E9' },
  capacity: { light: '#1BAF7A', dark: '#199E70' },
} as const;
export type Series = keyof typeof SERIES;
export const fill = (series: Series) => [
  { bgcolor: SERIES[series].light },
  (t: any) => t.applyStyles('dark', { bgcolor: SERIES[series].dark }),
];

// The key mirrors the mark: a square for a bar, a short stroke for a line.
export const Swatch: React.FC<{ series: Series; line?: boolean }> = ({ series, line }) => (
  <Box component="span" sx={[{ display: 'inline-block', width: line ? 12 : 8, height: line ? 2 : 8, borderRadius: line ? '1px' : '2px', mr: 0.5, flexShrink: 0 }, ...fill(series)]} />
);

export const Legend: React.FC<{ items: { series: Series; label: string }[]; line?: boolean }> = ({ items, line }) => (
  <Stack direction="row" gap={1.25} flexWrap="wrap" component="span">
    {items.map((i) => (
      <Typography key={i.series} variant="caption" color="text.secondary" component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
        <Swatch series={i.series} line={line} />{i.label}
      </Typography>
    ))}
  </Stack>
);

// Every week keeps its slot; only some are named, so labels never touch.
const labelStep = (n: number, wide?: boolean) => [1, 2, 4, 8].find((k) => n / k <= (wide ? 13 : 8)) || 8;

// Weeks along x with a real y axis: lines for a trend, columns for a
// count. A hairline grid, a crosshair that snaps to the nearest week and a
// tooltip listing every series there. The weeks are buttons laid over the
// plot, so a week is picked the same way everywhere, by pointer or key.
//
// Two of these stacked with the same weeks line up exactly — same gutter,
// same slots — which is how two measures on different scales share a card
// without a second axis.
export type LineSeries = { series: Series; label: string; values: (number | null)[]; mark?: 'line' | 'bar' };
export type LineWeek = { key: string; label: string; draft?: boolean; selected?: boolean; dim?: boolean };

function niceTicks(lo: number, hi: number, count = 4) {
  if (hi <= lo) hi = lo + 1;
  const raw = (hi - lo) / count;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((x) => x >= raw) || raw;
  const start = Math.floor(lo / step) * step;
  const ticks: number[] = [];
  for (let v = start; v <= hi + step * 0.001; v += step) ticks.push(Math.round(v * 1e6) / 1e6);
  if (ticks[ticks.length - 1] < hi) ticks.push(ticks[ticks.length - 1] + step);
  return ticks;
}

export const LineChart: React.FC<{
  title?: string;
  hint?: string;
  weeks: LineWeek[];
  series: LineSeries[];
  format: (n: number) => string;
  /** Magnitudes compared against each other start at zero; a trend can zoom. */
  zero?: boolean;
  height?: number;
  /** No card, for a row inside another chart's card. */
  bare?: boolean;
  /** Week names under the plot. On by default; off for the upper row of a pair. */
  xLabels?: boolean;
  onPick?: (key: string) => void;
  /** What a slot is called in its tooltip and to a screen reader. Weeks ending, by default. */
  slotName?: (w: LineWeek) => string;
}> = ({ title, hint, weeks, series, format, zero: zeroAsked, height = 170, bare, xLabels = !bare, onPick, slotName = (w) => `Week ending ${w.label}` }) => {
  const box = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(560);
  const [hover, setHover] = useState<number | null>(null);
  useEffect(() => {
    const el = box.current;
    if (!el) return undefined;
    const measure = () => { if (el.clientWidth) setWidth(el.clientWidth); };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const values = series.flatMap((x) => x.values).filter((v): v is number => v !== null);
  const lo = values.length ? Math.min(...values) : 0;
  const hi = values.length ? Math.max(...values) : 1;
  // A column always grows from zero. A line may zoom, but never so far that
  // a 2% wiggle fills the plot: the window is at least ~16% of the value.
  const zero = zeroAsked || series.some((x) => x.mark === 'bar');
  const pad = Math.max((hi - lo) * 0.15, Math.abs(hi) * 0.08) || 1;
  const ticks = niceTicks(zero ? 0 : Math.max(0, lo - pad), hi + (zero ? 0 : pad * 0.5), height < 120 ? 2 : 4);
  const y0 = ticks[0];
  const y1 = ticks[ticks.length - 1];
  const left = 44;
  const right = 16;
  // Room above the highest point for its value.
  const top = 18;
  const bottom = xLabels ? 22 : 6;
  const plotW = Math.max(10, width - left - right);
  const plotH = height - top - bottom;
  const slot = plotW / Math.max(1, weeks.length);
  const x = (i: number) => left + slot * (i + 0.5);
  const y = (v: number) => top + plotH - ((v - y0) / (y1 - y0 || 1)) * plotH;
  const labelEvery = labelStep(weeks.length, width > 700);
  const dots = weeks.length <= 13;
  // A value on every point while each week has room for one; past that,
  // only the newest, and the tooltip carries the rest.
  const everyValue = dots && slot >= 44;
  const path = (vals: (number | null)[]) => vals.reduce((d, v, i) => {
    if (v === null) return d;
    const prev = i > 0 ? vals[i - 1] : null;
    return `${d}${prev === null || i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(v).toFixed(1)}`;
  }, '');
  const strokeSx = Object.fromEntries(Object.entries(SERIES).flatMap(([k, c]) => [
    [`& .s-${k}`, { stroke: c.light, fill: c.light }],
  ]));
  const strokeDark = Object.fromEntries(Object.entries(SERIES).map(([k, c]) => [`& .s-${k}`, { stroke: c.dark, fill: c.dark }]));
  const h = hover;

  const plot = (
    <Box ref={box} sx={{ position: 'relative', mt: title ? 2 : 0 }} onMouseLeave={() => setHover(null)}>
      <Box
        component="svg"
        width="100%"
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={`${title || series.map((x) => x.label).join(' and ')} over time`}
        sx={[{ display: 'block', overflow: 'visible', ...strokeSx }, (t: any) => t.applyStyles('dark', strokeDark)]}
      >
        {weeks.map((w, i) => (w.selected ? (
          <Box component="rect" key={`sel${w.key}`} x={x(i) - slot / 2} y={top} width={slot} height={plotH} sx={{ fill: 'var(--mui-palette-action-selected)' }} />
        ) : null))}
        {ticks.map((t) => (
          <g key={t}>
            <Box component="line" x1={left} x2={left + plotW} y1={y(t)} y2={y(t)} sx={{ stroke: 'var(--mui-palette-divider)', strokeWidth: 1 }} />
            <Box component="text" x={left - 6} y={y(t)} dy="0.32em" textAnchor="end" sx={{ fill: 'var(--mui-palette-text-secondary)', fontSize: 11, fontVariantNumeric: 'tabular-nums' }}>{format(t)}</Box>
          </g>
        ))}
        {xLabels && weeks.map((w, i) => ((weeks.length - 1 - i) % labelEvery === 0 || w.selected ? (
          <Box component="text" key={`l${w.key}`} x={x(i)} y={height - 6} textAnchor="middle" sx={{ fill: w.selected ? 'var(--mui-palette-primary-dark)' : 'var(--mui-palette-text-secondary)', fontSize: 11, fontWeight: w.selected ? 700 : 400 }}>{w.label}</Box>
        ) : null))}
        {h !== null && <Box component="line" x1={x(h)} x2={x(h)} y1={top} y2={top + plotH} sx={{ stroke: 'var(--mui-palette-text-disabled)', strokeWidth: 1 }} />}
        {series.map((sr) => (
          <g key={sr.series} className={`s-${sr.series}`}>
            {sr.mark === 'bar' && sr.values.map((v, i) => {
              if (v === null || v <= 0) return null;
              // At most 24px, 4px round at the data end, square on the baseline.
              const bw = Math.min(24, slot * 0.6);
              const x0 = x(i) - bw / 2;
              const yt = y(v);
              const yb = y(y0);
              const r = Math.min(4, (yb - yt) / 2, bw / 2);
              return (
                <g key={i}>
                  <path
                    d={`M${x0},${yb}V${yt + r}Q${x0},${yt} ${x0 + r},${yt}H${x0 + bw - r}Q${x0 + bw},${yt} ${x0 + bw},${yt + r}V${yb}Z`}
                    stroke="none"
                    opacity={weeks[i].draft ? 0.55 : 1}
                  />
                  {dots && (
                    <Box component="text" x={x(i)} y={yt - 5} textAnchor="middle" sx={{ fill: 'var(--mui-palette-text-primary)', stroke: 'none', fontSize: 11, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{format(v)}</Box>
                  )}
                </g>
              );
            })}
            {sr.mark !== 'bar' && <path d={path(sr.values)} fill="none" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />}
            {sr.mark !== 'bar' && sr.values.map((v, i) => {
              if (v === null) return null;
              const last = i === sr.values.length - 1;
              if (!(dots || last || i === h || weeks[i].selected)) return null;
              // Hollow for a draft week: the number can still move.
              return (
                <Box
                  component="circle"
                  key={i}
                  cx={x(i)}
                  cy={y(v)}
                  r={4}
                  strokeWidth={2}
                  // A surface ring, so a dot stays legible where lines cross.
                  // A draft dot takes the series stroke and a hollow middle.
                  sx={{ stroke: 'var(--mui-palette-background-paper)' }}
                  className={weeks[i].draft ? `s-${sr.series}` : undefined}
                  style={weeks[i].draft ? { fill: 'var(--mui-palette-background-paper)' } : undefined}
                />
              );
            })}
          </g>
        ))}
        {/* Values, in text ink rather than the series colour. Where two lines
            share a week, the upper one is labelled above and the lower below. */}
        {series.filter((sr) => sr.mark !== 'bar').map((sr) => sr.values.map((v, i) => {
          if (v === null || !(everyValue || i === sr.values.length - 1)) return null;
          const others = series.filter((o) => o !== sr && o.mark !== 'bar').map((o) => o.values[i]).filter((o): o is number => o !== null);
          const above = others.every((o) => v >= o);
          return (
            <Box
              component="text"
              key={`v${sr.series}${i}`}
              x={x(i)}
              y={above ? y(v) - 9 : y(v) + 17}
              textAnchor="middle"
              sx={{ fill: 'var(--mui-palette-text-primary)', stroke: 'none', fontSize: 10.5, fontWeight: 600, fontVariantNumeric: 'tabular-nums', pointerEvents: 'none' }}
            >
              {format(v)}
            </Box>
          );
        }))}
      </Box>
      {/* The weeks as buttons over the plot: hover, focus and pick. */}
      <Stack direction="row" sx={{ position: 'absolute', left, top, width: plotW, height: plotH }}>
        {weeks.map((w, i) => (
          <Box
            key={w.key}
            component={onPick ? 'button' : 'div'}
            type={onPick ? 'button' : undefined}
            aria-pressed={onPick ? !!w.selected : undefined}
            aria-label={slotName(w)}
            onMouseEnter={() => setHover(i)}
            onFocus={() => setHover(i)}
            onBlur={() => setHover(null)}
            onClick={onPick ? () => onPick(w.key) : undefined}
            sx={{
              flex: 1, minWidth: 0, p: 0, border: 0, bgcolor: 'transparent', cursor: onPick ? 'pointer' : 'default',
              '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: -2 },
            }}
          />
        ))}
      </Stack>
      {h !== null && (
        <Card
          elevation={4}
          sx={{
            position: 'absolute', pointerEvents: 'none', top: 0, zIndex: 2, px: 1.25, py: 0.75, minWidth: 150,
            left: Math.min(Math.max(0, x(h) + 12), width - 170),
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>
            {slotName(weeks[h])}{weeks[h].draft ? ' (draft)' : ''}
          </Typography>
          {/* Highest first, so the rows stack the way the lines do. */}
          {[...series].sort((p, q) => (q.values[h] ?? -Infinity) - (p.values[h] ?? -Infinity)).map((sr) => (
            <Stack key={sr.series} direction="row" alignItems="center" justifyContent="space-between" gap={1.5}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'inline-flex', alignItems: 'center' }}><Swatch series={sr.series} line />{sr.label}</Typography>
              <Typography variant="caption" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{sr.values[h] === null ? '—' : format(sr.values[h] as number)}</Typography>
            </Stack>
          ))}
        </Card>
      )}
    </Box>
  );
  if (bare) return plot;
  return (
    <Card sx={{ p: 2.5, overflow: 'visible' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" gap={1} flexWrap="wrap">
        <Typography sx={{ fontWeight: 700 }}>{title}</Typography>
        {series.length > 1 ? <Legend line={series.every((x) => x.mark !== 'bar')} items={series} /> : <Typography variant="caption" color="text.secondary">{hint}</Typography>}
      </Stack>
      {plot}
    </Card>
  );
};

